import "./FormField.scss";

import clsx from "clsx";
import React from "react";
import { FieldValues, RegisterOptions } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ErrorMessage } from "@/components/Interface/FormFields/ErrorMessage/ErrorMessage";
import {
  extendFormOptions,
  FormFieldProps,
  getFormFieldError,
} from "@/components/Interface/FormFields/FormField/FormField.utils.ts";
import { getNestedValue } from "@/utils/objectUtils";

export const FormField = <TFormValues extends FieldValues>({
  id,
  name,
  label,
  ariaLabel,
  options = {
    required: true,
  },
  errors,
  register,
  type = "text",
  autocomplete,
  onFocus,
  onBlur,
  children,
  value,
  icon,
  passwordRules,
  className,
  inputMode,
  hideOptionalLabel,
  variant = "light",
  layout = "floating",
  suppressError = false,
}: React.PropsWithChildren<FormFieldProps<TFormValues>>) => {
  const { t } = useTranslation();

  // HTML maxLength blocks live typing; RHF maxLength catches pre-filled
  // values that exceed the limit (API data, defaultValues, reset).
  const maxLengthValue =
    typeof options?.maxLength === "number"
      ? options.maxLength
      : (options?.maxLength as { value?: number } | undefined)?.value;
  const optionsWithParsedMaxLength = extendFormOptions(options, t);
  const errorMessage: string = getFormFieldError(
    errors,
    name,
    optionsWithParsedMaxLength,
  );

  // Use getNestedValue to safely access nested field errors
  const fieldError = getNestedValue(errors, name);
  const hasError = fieldError !== undefined;
  const isDisabled = !!options?.disabled || !!options.readOnly;

  // Convention: required fields have no indicator; optional fields show "(optional)"
  const isOptional =
    !hideOptionalLabel &&
    !options?.required &&
    !options?.readOnly &&
    !options?.disabled;

  const optionalLabel = t([
    "translation:Common.label.optional",
    "Onboarding.sections.personal-details.form.optionalLabel",
  ]);

  const labelWithOptional =
    label && isOptional ? `${label} ${optionalLabel}` : (label ?? "");

  const {
    onBlur: registerOnBlur,
    onChange: registerOnChange,
    name: registerName,
    ref: registerRef,
  } = register(
    name,
    optionsWithParsedMaxLength as RegisterOptions<TFormValues>,
  );

  const inputElement = (
    <div
      className={clsx(
        "dire-input",
        variant === "dark" && "dire-input--dark",
        hasError && "invalid",
        isDisabled && "is-disabled",
      )}
    >
      <input
        id={id}
        aria-label={label ?? ariaLabel}
        aria-required={!!options?.required}
        aria-invalid={hasError ? "true" : "false"}
        autoComplete={autocomplete}
        name={registerName}
        ref={registerRef}
        onChange={registerOnChange}
        onBlur={(event) => {
          registerOnBlur(event);
          onBlur?.(event);
        }}
        {...(options.readOnly && { readOnly: true })}
        {...(options?.disabled && { disabled: true })}
        {...(type && { type })}
        {...(value && { value })}
        {...(passwordRules && { passwordrules: passwordRules })}
        {...(inputMode && { inputMode })}
        {...(maxLengthValue !== undefined && { maxLength: maxLengthValue })}
        data-testid={name}
        // Drives the `:placeholder-shown` selector that floats the label — never rendered.
        placeholder=" "
        {...(onFocus && { onFocus })}
        // `:placeholder-shown` never matches on date/month/week/time inputs (they don't
        // support the placeholder attribute), so those types rely on this class instead.
        className={clsx("dire-input__control", !!value && "has-value")}
      />
      {label && (
        <label htmlFor={id} className="dire-input__label">
          {labelWithOptional}
        </label>
      )}
      {icon && <div className="icon">{icon}</div>}
      {children}
    </div>
  );

  // Stacked layout: the form-field-container IS the visible grey box. The
  // ErrorMessage must live OUTSIDE that box (below it), so we wrap both in a
  // plain div. `suppressError` lets a parent (e.g. a shared error row for
  // side-by-side fields) take over error rendering.
  if (layout === "stacked") {
    return (
      <div>
        <div
          className={clsx(
            "form-field-container",
            "form-field-container--stacked",
            isDisabled && "is-disabled",
            hasError && "invalid",
            className,
            !!icon && "withIcon",
          )}
        >
          {inputElement}
        </div>
        {!suppressError && errorMessage && (
          <ErrorMessage message={errorMessage} name={name} />
        )}
      </div>
    );
  }

  return (
    <div
      className={clsx(
        "form-field-container",
        isDisabled && "is-disabled",
        hasError && "invalid",
        className,
        !!icon && "withIcon",
      )}
    >
      {inputElement}
      {!suppressError && errorMessage && (
        <ErrorMessage message={errorMessage} name={name} />
      )}
    </div>
  );
};
